/**
 * @file Chooses which bodies the walls open up for.
 *
 * The gameplay half of the contract owned by
 * {@link ../rendering/WallCutout.js WallCutout}: this queries the world for
 * characters worth keeping visible and hands over world-space points. It knows
 * nothing about shaders; the cutout knows nothing about the ECS. Whether a
 * wall is actually in the way is not decided here either — the cone test in
 * the shader answers that per fragment, so a character standing in the open
 * costs a slot and opens nothing.
 */

import { System } from '../ecs/System.js';
import { Transform, Health, AIBrain } from '../components/index.js';

export class WallCutoutSystem extends System {
  /** Presentation, after movement has settled and before the frame is drawn. */
  static priority = 95;

  constructor() {
    super();
    /**
     * Enemy candidates and their distance to the player, reused every frame so
     * the ranking below never allocates.
     * @type {{ x: number, y: number, z: number, distanceSq: number }[]}
     * @private
     */
    this._pool = [];
    /** @type {number} Live entries in {@link WallCutoutSystem#_pool}. @private */
    this._count = 0;
  }

  /**
   * @param {number} _dt Delta time in seconds; every point is read from current
   *   component state, so nothing here integrates.
   * @returns {void}
   */
  update(_dt) {
    // Rebuilt with each level, so it is re-read rather than cached.
    const cutout = this.ctx.arena?.wallCutout;
    if (!cutout) return;

    const settings = this.ctx.config.wallCutout;
    if (!settings?.enabled) {
      cutout.clear();
      return;
    }
    cutout.applySettings(settings);

    const lift = settings.centerHeight ?? 0.9;
    const budget = Math.max(1, Math.round(settings.maxCharacters ?? 8));

    cutout.begin();

    // The player takes the first slot unconditionally: losing sight of your own
    // body behind a wall is the failure this whole feature exists to prevent.
    const player = this.ctx.player;
    const origin = player?.alive ? player.get(Transform)?.position : null;
    if (origin) cutout.add(origin.x, origin.y + lift, origin.z);

    this._collectEnemies(origin, lift);
    this._addNearest(cutout, origin ? budget - 1 : budget);

    cutout.end();
  }

  /**
   * Gathers every live enemy into the scratch pool.
   *
   * @param {{ x: number, z: number } | null} origin Player position, or null
   *   when there is none — then distance is not used and order is arbitrary.
   * @param {number} lift Height above the feet the hole centres on.
   * @returns {void}
   * @private
   */
  _collectEnemies(origin, lift) {
    this._count = 0;

    for (const enemy of this.world.query(AIBrain, Transform, Health)) {
      if (!enemy.alive || enemy.get(Health).dead) continue;

      const position = enemy.get(Transform).position;
      const slot = this._take();
      slot.x = position.x;
      slot.y = position.y + lift;
      slot.z = position.z;

      if (origin) {
        const dx = position.x - origin.x;
        const dz = position.z - origin.z;
        slot.distanceSq = dx * dx + dz * dz;
      } else {
        slot.distanceSq = 0;
      }
    }
  }

  /**
   * Hands over the nearest candidates, closest first.
   *
   * Selection rather than a sort: `budget` is a handful and the pool can hold
   * a wave's worth of enemies, so repeatedly plucking the minimum is both
   * cheaper and free of the allocation a comparator sort would make.
   *
   * @param {import('../rendering/WallCutout.js').WallCutout} cutout Sink.
   * @param {number} budget Slots left to fill.
   * @returns {void}
   * @private
   */
  _addNearest(cutout, budget) {
    for (let taken = 0; taken < budget && this._count > 0; taken++) {
      let best = 0;
      for (let i = 1; i < this._count; i++) {
        if (this._pool[i].distanceSq < this._pool[best].distanceSq) best = i;
      }

      const slot = this._pool[best];
      cutout.add(slot.x, slot.y, slot.z);

      // Swap-remove: the pool is unordered, so the tail can fill the gap.
      this._pool[best] = this._pool[this._count - 1];
      this._pool[this._count - 1] = slot;
      this._count--;
    }
  }

  /**
   * @returns {{ x: number, y: number, z: number, distanceSq: number }} A pooled
   *   slot, grown on demand and never released.
   * @private
   */
  _take() {
    if (this._count === this._pool.length) {
      this._pool.push({ x: 0, y: 0, z: 0, distanceSq: 0 });
    }
    return this._pool[this._count++];
  }
}
